import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { IProduct } from '@/store/ducks/products/types';

import {
  getBestSellers,
  getCategoryProducts,
  getClientOffers,
  getGiftClubProducts,
  getGroupProducts,
  getProductsInOffer,
  getRandomProducts,
  getSuperOffersProducts,
} from '../../../routes/products';
import { ClientTypes } from '../../../services/clients';
import { getProductListStateName } from '../../../services/products';
import { ProductListInWorld } from '../../../services/worlds';
import { ApplicationState } from '../../../store';
import { IProductList } from '../../../store/ducks/productLists/types';
import { ITemplateProductList } from '../../../store/ducks/settings/types';
import { IWorld } from '../../../store/ducks/worlds/types';
import { useSmartTextColor } from '../../hooks/useSmartTextColor';
import { useWebContext } from '../../hooks/useWebContext';
import ListProductsLoad from '../loads/ListProductsLoad';

const Products = lazy(() => import('../products/Products'));

interface Props {
  position: number;
  world?: IWorld | null;
  componentType?: 'default' | 'world';
  worldIsLoading?: boolean | undefined;
  productList: ITemplateProductList[] | undefined | null;
}

function TemplateProductLists({
  position,
  productList,
  worldIsLoading,
  componentType = 'default',
  world,
}: Props) {
  const { order } = useWebContext();
  const color = useSmartTextColor('#666');

  const [products, setProducts] = useState<IProduct[]>();

  const list = productList?.find((list) => list.position === position);
  const selectProducts = useSelector<ApplicationState, IProductList | null>((state) => {
    const name = getProductListStateName(list?.type);

    if (!name) return null;
    return state.productLists[name];
  });

  const getProducts = () => {
    if (componentType === 'world' && world) return list ? world[ProductListInWorld[list.type]] : [];

    return list ? selectProducts?.products : [];
  };

  const isLoading = worldIsLoading ?? selectProducts?.loading;

  const getProductsByListType = useCallback(
    (list: ITemplateProductList) => {
      if (order?.dst_shop_id) {
        switch (list.type) {
          case 'offers':
            getProductsInOffer(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
            );
            break;
          case 'best-sales':
            getBestSellers(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
            );
            break;
          case 'offers-for-you':
            if (order.client.type !== ClientTypes.ANONYMOUS) {
              getClientOffers(
                order.client.id,
                order.dst_shop_id,
                list.limit || 30,
                +list.show_striped_medicine_products,
              );
            }

            break;
          case 'random-offers':
            getRandomProducts(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
            );
            break;
          case 'super-offers':
            getSuperOffersProducts(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
            );
            break;
          case 'gift-club':
            getGiftClubProducts(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
            );
            break;
          case 'category':
            if (!list.category_id) return;

            getCategoryProducts(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
              list.category_id,
            );
            break;
          case 'group':
            if (!list.group_id) return;

            getGroupProducts(
              order.dst_shop_id,
              list.limit || 30,
              +list.show_striped_medicine_products,
              list.group_id,
            );
            break;
          default:
            break;
        }
      }
    },
    [order?.dst_shop_id],
  );

  useEffect(() => {
    if (list && selectProducts && componentType === 'default' && order?.dst_shop_id) {
      getProductsByListType(list);
    }
  }, [order?.client.type, order?.dst_shop_id]);

  useEffect(() => {
    if (!selectProducts?.loading || (world && !worldIsLoading)) setProducts(getProducts());
  }, [order?.client.type, order?.dst_shop_id, selectProducts?.loading, world, worldIsLoading]);

  return (
    <>
      {!!isLoading && list?.type !== 'offers-for-you' && <ListProductsLoad />}

      {list && selectProducts && !isLoading && (
        <section>
          {products && products.length > 0 && (
            <h1 className="section-header" style={{ color }}>
              {list.title}
            </h1>
          )}

          <Suspense fallback={<div />}>
            <Products products={products} keyName={list.type} />
          </Suspense>
        </section>
      )}
    </>
  );
}

export default TemplateProductLists;
